import type { GuideStep, GuideAction, UIActions } from "./types.js";
import React, { useRef, useState } from "react";
import { STEPS, FAQS } from "./guide-data.js";
function Illustration({ step }: { step: GuideStep }) {
  if (step.visual === "import")
    return (
      <>
        <div className="guide-files">
          <span>
            WAV <i>↘</i>
          </span>
          <span>
            FLAC <i>↘</i>
          </span>
          <span>
            MP3 <i>↘</i>
          </span>
        </div>
        <div className="guide-file-home">
          crate<span>.</span>
          <small>YOUR SOUND LIVES HERE</small>
        </div>
      </>
    );
  if (step.visual === "tags")
    return (
      <>
        <div className="guide-tag-card">
          <span>TRACK DETAILS / EXAMPLE</span>
          <strong>Midnight feeling</strong>
          <div>
            <span>
              GENRE<b>House; Disco</b>
            </span>
            <span>
              BPM<b>124</b>
            </span>
            <span>
              KEY<b>A minor</b>
            </span>
          </div>
          <small>✓ A little more you.</small>
        </div>
      </>
    );
  if (step.visual === "set")
    return (
      <>
        <div className="guide-set-stack">
          <span></span>
          <span></span>
          <div>
            AFTER
            <br />
            HOURS<span>VOL. 01 / YOUR SELECTION</span>
            <i>↗</i>
          </div>
        </div>
      </>
    );
  return (
    <>
      <div className="guide-backup-file">
        <span>↗</span>
        <strong>
          THE WHOLE
          <br />
          GOOD THING.
        </strong>
        <small>CRATE FULL BACKUP / .ZIP</small>
      </div>
      <div className="guide-backup-caption">Music + details + every set</div>
    </>
  );
}

export function Guide({
  hidden,
  actions,
}: {
  hidden: boolean;
  actions: UIActions;
}) {
  const [index, setIndex] = useState(0),
    tabs = useRef<Array<HTMLButtonElement | null>>([]),
    step = STEPS[index] ?? STEPS[0];
  const select = (next: number, focus = false) => {
    setIndex(next);
    if (focus) tabs.current[next]?.focus();
  };
  const action = (key: GuideAction) => () => {
    const callbacks = {
      add: actions.addMusic,
      library: () => actions.navigate("all"),
      create: actions.createSet,
      backup: actions.backupDialog,
    };
    Promise.resolve(callbacks[key]()).catch(actions.reportError);
  };
  return (
    <section
      id="how-it-works"
      className={`how-it-works${hidden ? " hidden" : ""}`}
      aria-labelledby="guide-title"
    >
      <div className="guide-topline">
        <span>THE CRATE FIELD GUIDE</span>
        <span>FOUR STEPS. ALL YOU.</span>
      </div>
      <div className="guide-hero">
        <div className="guide-hero-copy">
          <span className="guide-kicker">HOW THIS WORKS</span>
          <h1 id="guide-title" tabIndex={-1}>
            From your files.
            <br />
            To your <em>frequency.</em>
          </h1>
          <p>
            A home for your music. A place to shape your next set. Here’s how to
            make Crate yours.
          </p>
          <a
            className="button primary"
            href="#guide-walkthrough"
            id="guide-start"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("guide-walkthrough")?.scrollIntoView({
                behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
                  ? "instant"
                  : "smooth",
                block: "start",
              });
              tabs.current[0]?.focus({ preventScroll: true });
            }}
          >
            Let’s take a spin <span aria-hidden="true">↓</span>
          </a>
          <span className="guide-local">
          Your music stays on your device.
          </span>
        </div>
        <div className="guide-hero-art" aria-hidden="true">
          <div className="guide-vinyl">
            <span>
              CRATE
              <br />
              <small>33⅓ RPM</small>
            </span>
          </div>
          <div className="guide-sleeve">
            <span>
              LESS SCROLL.
              <br />
              MORE SOUL.
            </span>
            <small>CRATE® / MADE FOR YOUR NEXT SET</small>
            <i>↗</i>
          </div>
          <span className="guide-art-edition">
            YOUR SOUND. ON YOUR TERMS. / VOL. 01
          </span>
        </div>
      </div>
      <div className="guide-strip">
        <span>COLLECT.</span>
        <i>✳</i>
        <span>CURATE.</span>
        <i>✳</i>
        <span>CREATE.</span>
        <i>✳</i>
        <span>REPEAT.</span>
        <i>✳</i>
      </div>
      <section
        className="guide-walkthrough"
        id="guide-walkthrough"
        aria-labelledby="guide-steps-title"
      >
        <div className="guide-section-heading">
          <h2 id="guide-steps-title">Your next set starts here.</h2>
          <span>A LITTLE TOUR, AT YOUR TEMPO</span>
        </div>
        <div className="guide-tabs" role="tablist" aria-label="How Crate works">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              ref={(el) => {
                tabs.current[i] = el;
              }}
              id={`guide-tab-${i}`}
              role="tab"
              aria-selected={i === index}
              aria-controls="guide-panel"
              tabIndex={i === index ? 0 : -1}
              data-guide-step={i}
              onClick={() => select(i)}
              onKeyDown={(e) => {
                const keys: Partial<Record<string, number>> = {
                  ArrowRight: (i + 1) % STEPS.length,
                  ArrowLeft: (i + STEPS.length - 1) % STEPS.length,
                  Home: 0,
                  End: STEPS.length - 1,
                };
                const next = keys[e.key];
                if (next !== undefined) {
                  e.preventDefault();
                  select(next, true);
                }
              }}
            >
              <span>0{i + 1}</span>
              {s.label}
              <span aria-hidden="true">↗</span>
            </button>
          ))}
        </div>
        <div
          className="guide-panel"
          id="guide-panel"
          role="tabpanel"
          aria-labelledby={`guide-tab-${index}`}
          tabIndex={0}
        >
          <div className="guide-step-copy">
            <span className="guide-kicker">
              SIDE {String(index + 1).padStart(2, "0")} / {step.stamp}
            </span>
            <h3>{step.title}</h3>
            <p>{step.copy}</p>
            <button
              className="button primary"
              data-guide-action={step.key}
              onClick={action(step.key)}
            >
              {step.action} <span aria-hidden="true">↗</span>
            </button>
            <small>{step.note}</small>
          </div>
          <div
            className={`guide-step-art guide-art-${step.visual}`}
            aria-hidden="true"
          >
            <Illustration step={step} />
            <span className="guide-art-caption">{step.detail}</span>
          </div>
        </div>
      </section>
      <section className="guide-faq" aria-labelledby="guide-faq-title">
        <div>
          <span className="guide-kicker">THE SMALL PRINT, SIMPLIFIED.</span>
          <h2 id="guide-faq-title">
            Good questions.
            <br />
            Straight answers.
          </h2>
          <p>
            Less figuring it out.
            <br />
            More finding your sound.
          </p>
        </div>
        <div className="guide-questions">
          {FAQS.map(([q, a]) => (
            <details key={q}>
              <summary>
                {q}
                <span aria-hidden="true">+</span>
              </summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>
      <div className="guide-finale">
        <span className="guide-kicker">YOU BRING THE TASTE.</span>
        <h2>
          Give your sound
          <br />
          some space.
        </h2>
        <button
          className="button"
          data-guide-action="add"
          onClick={action("add")}
        >
          Add your music <span aria-hidden="true">↗</span>
        </button>
        <button
          className="guide-demo-link"
          data-guide-action="library"
          onClick={action("library")}
        >
          Or explore the library →
        </button>
        <span className="guide-finale-mark" aria-hidden="true">
          ✳
        </span>
      </div>
      <div className="guide-colophon">
        <span>crate.</span>
        <span>KEEP THE ORIGINALS. FIND THE FLOW.</span>
        <button data-view="home" onClick={() => actions.navigate("home")}>
          Back to your space ↑
        </button>
      </div>
    </section>
  );
}
